/**
 * useCurrentUser — single seam for "who is the current user?".
 *
 * Migrated from Supabase Auth to Clerk (Step 3 of Clerk migration).
 *
 * When `VITE_FLAG_AUTH_ENABLED=false`, returns MOCK_USER so tests and
 * demo mode work without a real Clerk session.
 *
 * Public contract (unchanged from Supabase era):
 *   - user            — auth identity (id, email, name, role)
 *   - profile         — always null (Supabase profiles table is RLS-gated;
 *                        will be re-wired once JWT secret is configured)
 *   - isLoading       — true until Clerk session resolution completes
 *   - isAdmin         — derived from user.publicMetadata.role === "admin"
 *   - isAuthenticated — derived from user !== null
 *   - isLoggingOut    — true while sign-out is in-flight
 *   - signOut         — calls Clerk signOut, guards against double-click
 *
 * Spec: docs/architecture/00-planning/03-auth-strategy.md
 */

import { useRef, useState } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";
import { isFeatureEnabled } from "@/lib/flags";
import { useSupabaseClient } from "@/lib/supabase";
import type { BillingMode, Database, ProfileRole } from "@/types/supabase";
import type { SupabaseClient } from "@supabase/supabase-js";

export type UserRole = "admin" | "user" | "guest";

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  /**
   * Optional professional specialty (e.g. "Ortopedia").
   * Nullable because a guest has none.
   */
  specialty?: string;
}

export type Profile = Database["public"]["Tables"]["profiles"]["Row"];

export interface UseCurrentUserResult {
  /** `null` means not authenticated. */
  user: CurrentUser | null;
  /** Full profile row — null until Supabase JWT bridge is configured. */
  profile: Profile | null;
  /** True while session/profile is resolving. */
  isLoading: boolean;
  /** Derived from user.publicMetadata.role === "admin". */
  isAdmin: boolean;
  /** Derived: `user !== null`. */
  isAuthenticated: boolean;
  /** True while a sign-out request is in-flight. */
  isLoggingOut: boolean;
  /** Sign the current user out. Guards against double-click races. */
  signOut: () => Promise<{ error: Error | null }>;
  /**
   * Supabase client authenticated via the Clerk → Supabase JWT bridge.
   * Use for any RLS-gated query — `auth.uid()` will resolve to the
   * internal user UUID from the profiles table.
   */
  supabaseClient: SupabaseClient<Database>;
}

export const MOCK_USER: CurrentUser = {
  id: "mock-user-1",
  name: "Dr. Marcel Pita",
  email: "marcel.pita@example.com",
  role: "user",
  specialty: "Ortopedia",
} as const satisfies CurrentUser;

const MOCK_PROFILE: Profile = {
  id: MOCK_USER.id,
  email: MOCK_USER.email,
  display_name: MOCK_USER.name,
  honorific_prefix: "Dr.",
  role: "user" satisfies ProfileRole,
  billing_mode: "platform" satisfies BillingMode,
  created_at: new Date(0).toISOString(),
  updated_at: new Date(0).toISOString(),
};

export function useCurrentUser(): UseCurrentUserResult {
  const authEnabled = isFeatureEnabled("AUTH_ENABLED");
  const { user: clerkUser, isLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  /** Guard: prevents concurrent sign-out calls from double-click. */
  const isSigningOut = useRef(false);
  /** Supabase client authenticated via Clerk → Supabase JWT bridge. */
  const { client: supabaseClient } = useSupabaseClient();

  /**
   * Sign out the current user.
   * Guards against double-click races via `isSigningOut` ref.
   */
  const signOut = async (): Promise<{ error: Error | null }> => {
    if (isSigningOut.current) {
      return { error: null };
    }
    isSigningOut.current = true;
    setIsLoggingOut(true);
    try {
      await clerkSignOut();
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    } finally {
      isSigningOut.current = false;
      setIsLoggingOut(false);
    }
  };

  // When auth is disabled, return mock user (preserves test/demo seam).
  if (!authEnabled) {
    return {
      user: MOCK_USER,
      profile: MOCK_PROFILE,
      isLoading: false,
      isAdmin: MOCK_USER.role === "admin",
      isAuthenticated: true,
      isLoggingOut: false,
      signOut,
      supabaseClient,
    };
  }

  // Clerk is still loading its session
  if (!isLoaded) {
    return {
      user: null,
      profile: null,
      isLoading: true,
      isAdmin: false,
      isAuthenticated: false,
      isLoggingOut,
      signOut,
      supabaseClient,
    };
  }

  // No authenticated Clerk user
  if (!clerkUser) {
    return {
      user: null,
      profile: null,
      isLoading: false,
      isAdmin: false,
      isAuthenticated: false,
      isLoggingOut,
      signOut,
      supabaseClient,
    };
  }

  // Map Clerk user to our CurrentUser shape
  const role: UserRole = clerkUser.publicMetadata?.role === "admin" ? "admin" : "user";
  const email = clerkUser.primaryEmailAddress?.emailAddress ?? "";
  const name = clerkUser.fullName ?? clerkUser.firstName ?? email.split("@")[0] ?? "Usuário";

  const currentUser: CurrentUser = {
    id: clerkUser.id,
    name,
    email,
    role,
  };

  return {
    user: currentUser,
    profile: null, // Will be wired once Supabase JWT bridge is configured
    isLoading: false,
    isAdmin: role === "admin",
    isAuthenticated: true,
    isLoggingOut,
    signOut,
    supabaseClient,
  };
}
